import type {GuideLevelFilterInput, GuideLevelSortInput} from 'gql/types';
import {GuideLevelSortField, SkillPathStatus, SortOrder} from 'gql/types';
import type {SortValue} from 'client/TeacherDashboard/types';

import {dueWithinBounds} from '../dueWindow';
import type {DueWithinFilter, SkillPathSortValue, SkillPathStatusSegment} from '../types';

export const STATUS_ENUM: Record<SkillPathStatusSegment, SkillPathStatus> = {
  open: SkillPathStatus.Open,
  closed: SkillPathStatus.Closed,
  scheduled: SkillPathStatus.Scheduled
};

/** Everything except the status segment — shared by the visible list and the per-status counts. */
export interface SharedSkillPathFilters {
  search: string;
  /** Subject id, or `'all'`. */
  subject: string;
  dueWithin: DueWithinFilter;
  dueDateStart: string;
  dueDateEnd: string;
}

/**
 * `null` means All Classes, which the server reads as the teacher's active classes. An empty array
 * is NOT the same thing — it scopes to no classroom at all and returns nothing.
 */
export const toClassroomIds = (classroomId?: string): string[] | null =>
  classroomId ? [classroomId] : null;

/**
 * The date bounds are evaluated against the recipient groups holding `status`, and match on overlap:
 * a skill belongs in the window when the range spanned by its due dates for that status intersects
 * it. Note this is weaker than "something is due in the window" — a skill with groups due in January
 * and December matches a June window. See nexus#20860; changing it is a product decision, so
 * `skillPathDueRangeOverlaps` deliberately preserves the existing behaviour.
 */
export const buildEntryFilter = (
  {search, subject, dueWithin, dueDateStart, dueDateEnd}: SharedSkillPathFilters,
  segment: SkillPathStatusSegment
): GuideLevelFilterInput => {
  const status = STATUS_ENUM[segment];
  const filter: GuideLevelFilterInput = {skillPathStatus: {eq: status}};

  const searchTerm = search.trim();
  if (searchTerm) {
    // Server-side across name and code, per the spec — not a name-only match.
    filter.matchesSearch = {eq: true, input: {search: searchTerm}};
  }
  if (subject !== 'all') {
    filter.subjectId = {eq: subject};
  }

  // One predicate rather than a min/max pair: same answer, but the server stops at the first group
  // that satisfies each bound instead of aggregating over every group on the skill.
  const {start, end} = dueWithinBounds(dueWithin, dueDateStart, dueDateEnd);
  if (start || end) {
    filter.skillPathDueRangeOverlaps = {
      eq: true,
      input: {
        status,
        onOrAfter: start?.toISOString() ?? null,
        onOrBefore: end?.toISOString() ?? null
      }
    };
  }
  return filter;
};

/** Every option sends an explicit sort, so the direction toggle means something for all of them. */
export const buildEntrySort = (
  {field, order}: SortValue<SkillPathSortValue>,
  segment: SkillPathStatusSegment
): GuideLevelSortInput[] => {
  const sortOrder = order === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  const status = STATUS_ENUM[segment];

  switch (field) {
    case 'recentlyEdited':
      return [
        {field: GuideLevelSortField.SkillPathLastEditedAt, order: sortOrder},
        {field: GuideLevelSortField.Name, order: SortOrder.Asc}
      ];
    case 'name':
      return [{field: GuideLevelSortField.Name, order: sortOrder}];
    case 'startDate':
      return [
        {
          field: GuideLevelSortField.SkillPathMinStartDate,
          order: sortOrder,
          skillPathMinStartDateInput: {status}
        },
        {field: GuideLevelSortField.Name, order: SortOrder.Asc}
      ];
    case 'dueDate':
    default:
      return [
        {
          field: GuideLevelSortField.SkillPathMinDueDate,
          order: sortOrder,
          skillPathMinDueDateInput: {status}
        },
        {field: GuideLevelSortField.Name, order: SortOrder.Asc}
      ];
  }
};
